import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Store,
  Utensils,
  Ticket,
  Sparkles,
  CreditCard,
  Receipt,
  LayoutGrid,
  Percent,
  HandCoins,
} from "lucide-react";
import { adminService } from "@/services/adminService";
import type { AdminActivityListRow } from "@/services/adminService";
import { adminProfilePathForRole } from "@/lib/adminProfilePaths";
import { ActivityCategory, ActivityPaymentModel } from "@/types";
import {
  countByStatusFilter,
  matchesStatusFilter,
  type StatusFilter,
} from "@/components/admin/adminListStatus";
import {
  DataTable,
  DataTablePrimaryText,
  type DataTableColumn,
} from "@/components/table";
import { AdminInactiveStatusDot } from "@/components/admin/AdminInactiveStatusDot";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import { AdminStatusFilter } from "@/components/admin/AdminStatusFilter";
import { cn } from "@/lib/utils";
import { matchesTextQuery } from "@/lib/textSearch";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import { AdminListToolbar, buildListResetToken, PageFrame } from "@/components/common";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";

type CategoryFilter = "all" | ActivityCategory;
type PaymentModelFilter = "all" | ActivityPaymentModel;
type DiscountFilter = "all" | "active";
type SortColumn =
  "category" | "name" | "owner" | "paymentModel" | "region" | "commission";

const CATEGORY_ICON_CONFIG: Record<
  ActivityCategory,
  { icon: typeof Store; className: string }
> = {
  [ActivityCategory.RESTAURANT]: {
    icon: Utensils,
    className: "bg-warning-50 text-warning-600 ring-warning-600/20",
  },
  [ActivityCategory.ATTRACTION]: {
    icon: Ticket,
    className: "bg-violet-50 text-violet-600 ring-violet-600/15",
  },
  [ActivityCategory.GENERIC_SERVICE]: {
    icon: Sparkles,
    className: "bg-primary-50 text-primary-600 ring-primary-600/15",
  },
};

const CATEGORY_LABEL_KEYS: Record<ActivityCategory, string> = {
  [ActivityCategory.RESTAURANT]: "categories.restaurant",
  [ActivityCategory.ATTRACTION]: "categories.attraction",
  [ActivityCategory.GENERIC_SERVICE]: "categories.genericService",
};

const PAYMENT_MODEL_CONFIG: Record<
  ActivityPaymentModel,
  { icon: typeof Store; labelKey: string }
> = {
  [ActivityPaymentModel.UPFRONT]: {
    icon: CreditCard,
    labelKey: "paymentModels.upfront",
  },
  [ActivityPaymentModel.RECEIPT_BASED]: {
    icon: Receipt,
    labelKey: "paymentModels.receiptBased",
  },
};

export default function AdminActivitiesPage() {
  const { t, i18n } = useTranslation("adminActivities");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [visibilityFilter, setVisibilityFilter] =
    useState<StatusFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
  const [paymentModelFilter, setPaymentModelFilter] =
    useState<PaymentModelFilter>("all");
  const [discountFilter, setDiscountFilter] = useState<DiscountFilter>("all");
  const [regionFilter, setRegionFilter] =
    useState<AdminRegionFilterValue>(null);

  const activityDisplayName = useCallback(
    (activity: AdminActivityListRow) =>
      resolveCatalogTranslation(activity.name_translations, i18n.language),
    [i18n.language],
  );

  const {
    data: activities = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "activities", regionFilter],
    queryFn: () =>
      adminService.getActivities({ region_id: regionFilter ?? undefined }),
  });

  const searchMatched = useMemo(() => {
    if (!searchQuery.trim()) return activities;
    return activities.filter((activity) =>
      matchesTextQuery(searchQuery, [
        activityDisplayName(activity),
        activity.owner_name,
      ]),
    );
  }, [activities, searchQuery, activityDisplayName]);

  type ActivityFilterOmit =
    "status" | "visibility" | "category" | "discount" | "paymentModel" | null;

  const filterActivities = useCallback(
    (omit: ActivityFilterOmit = null) =>
      searchMatched.filter((activity) => {
        if (
          omit !== "status" &&
          !matchesStatusFilter(activity.is_active, statusFilter)
        ) {
          return false;
        }
        if (
          omit !== "visibility" &&
          !matchesStatusFilter(activity.is_visible, visibilityFilter)
        ) {
          return false;
        }
        if (
          omit !== "category" &&
          categoryFilter !== "all" &&
          activity.category !== categoryFilter
        ) {
          return false;
        }
        if (
          omit !== "discount" &&
          discountFilter !== "all" &&
          !activity.has_active_discount
        ) {
          return false;
        }
        if (
          omit !== "paymentModel" &&
          paymentModelFilter !== "all" &&
          activity.payment_model !== paymentModelFilter
        ) {
          return false;
        }
        return true;
      }),
    [
      searchMatched,
      statusFilter,
      visibilityFilter,
      categoryFilter,
      discountFilter,
      paymentModelFilter,
    ],
  );

  const filteredActivities = useMemo(
    () => filterActivities(null),
    [filterActivities],
  );

  const statusFilterCounts = useMemo(
    () =>
      countByStatusFilter(
        filterActivities("status"),
        (activity) => activity.is_active,
      ),
    [filterActivities],
  );

  const visibilityFilterCounts = useMemo(
    () =>
      countByStatusFilter(
        filterActivities("visibility"),
        (activity) => activity.is_visible,
      ),
    [filterActivities],
  );

  const categoryFilterCounts = useMemo(() => {
    const base = filterActivities("category");
    return {
      all: base.length,
      [ActivityCategory.RESTAURANT]: base.filter(
        (activity) => activity.category === ActivityCategory.RESTAURANT,
      ).length,
      [ActivityCategory.ATTRACTION]: base.filter(
        (activity) => activity.category === ActivityCategory.ATTRACTION,
      ).length,
      [ActivityCategory.GENERIC_SERVICE]: base.filter(
        (activity) => activity.category === ActivityCategory.GENERIC_SERVICE,
      ).length,
    };
  }, [filterActivities]);

  const paymentModelFilterCounts = useMemo(() => {
    const base = filterActivities("paymentModel");
    return {
      all: base.length,
      [ActivityPaymentModel.UPFRONT]: base.filter(
        (activity) => activity.payment_model === ActivityPaymentModel.UPFRONT,
      ).length,
      [ActivityPaymentModel.RECEIPT_BASED]: base.filter(
        (activity) =>
          activity.payment_model === ActivityPaymentModel.RECEIPT_BASED,
      ).length,
    };
  }, [filterActivities]);

  const discountFilterCounts = useMemo(() => {
    const base = filterActivities("discount");
    return {
      all: base.length,
      active: base.filter((activity) => activity.has_active_discount).length,
    };
  }, [filterActivities]);

  const categoryOptions: {
    value: CategoryFilter;
    label: string;
    count: number;
    icon?: typeof Store;
    iconBadgeClassName?: string;
  }[] = [
    {
      value: "all",
      label: t("filters.categoryAll"),
      count: categoryFilterCounts.all,
    },
    {
      value: ActivityCategory.RESTAURANT,
      label: t("categories.restaurant"),
      count: categoryFilterCounts[ActivityCategory.RESTAURANT],
      icon: CATEGORY_ICON_CONFIG[ActivityCategory.RESTAURANT].icon,
      iconBadgeClassName:
        CATEGORY_ICON_CONFIG[ActivityCategory.RESTAURANT].className,
    },
    {
      value: ActivityCategory.ATTRACTION,
      label: t("categories.attraction"),
      count: categoryFilterCounts[ActivityCategory.ATTRACTION],
      icon: CATEGORY_ICON_CONFIG[ActivityCategory.ATTRACTION].icon,
      iconBadgeClassName:
        CATEGORY_ICON_CONFIG[ActivityCategory.ATTRACTION].className,
    },
    {
      value: ActivityCategory.GENERIC_SERVICE,
      label: t("categories.genericService"),
      count: categoryFilterCounts[ActivityCategory.GENERIC_SERVICE],
      icon: CATEGORY_ICON_CONFIG[ActivityCategory.GENERIC_SERVICE].icon,
      iconBadgeClassName:
        CATEGORY_ICON_CONFIG[ActivityCategory.GENERIC_SERVICE].className,
    },
  ];

  const paymentModelOptions: {
    value: PaymentModelFilter;
    label: string;
    count: number;
    icon?: typeof Store;
  }[] = [
    {
      value: "all",
      label: t("filters.paymentModelAll"),
      count: paymentModelFilterCounts.all,
    },
    {
      value: ActivityPaymentModel.UPFRONT,
      label: t("paymentModels.upfront"),
      count: paymentModelFilterCounts[ActivityPaymentModel.UPFRONT],
      icon: PAYMENT_MODEL_CONFIG[ActivityPaymentModel.UPFRONT].icon,
    },
    {
      value: ActivityPaymentModel.RECEIPT_BASED,
      label: t("paymentModels.receiptBased"),
      count: paymentModelFilterCounts[ActivityPaymentModel.RECEIPT_BASED],
      icon: PAYMENT_MODEL_CONFIG[ActivityPaymentModel.RECEIPT_BASED].icon,
    },
  ];

  const renderPaymentModelCell = (paymentModel: ActivityPaymentModel) => {
    const config = PAYMENT_MODEL_CONFIG[paymentModel];
    const Icon = config.icon;
    return (
      <span className="inline-flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-surface-400" aria-hidden />
        <span className="break-words text-sm text-surface-700">
          {t(config.labelKey)}
        </span>
      </span>
    );
  };

  const renderCategoryCell = (category: ActivityCategory) => {
    const config = CATEGORY_ICON_CONFIG[category];
    const Icon = config.icon;
    return (
      <span className="inline-flex items-center gap-2">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-inset",
            config.className,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <span className="break-words text-sm text-surface-700">
          {t(CATEGORY_LABEL_KEYS[category])}
        </span>
      </span>
    );
  };

  const columns = useMemo<DataTableColumn<AdminActivityListRow, SortColumn>[]>(
    () => [
      {
        id: "category",
        header: t("columns.category"),
        sortKey: "category",
        sortKind: "text",
        getSortValue: (row) => row.category,
        defaultWidth: 140,
        cell: (activity) => renderCategoryCell(activity.category),
      },
      {
        id: "name",
        header: t("columns.name"),
        sortKey: "name",
        sortKind: "text",
        getSortValue: (row) => activityDisplayName(row),
        defaultWidth: 220,
        cell: (activity) => (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <DataTablePrimaryText>
              {activityDisplayName(activity)}
            </DataTablePrimaryText>
            {activity.is_active ? null : <AdminInactiveStatusDot />}
          </span>
        ),
      },
      {
        id: "owner",
        header: t("columns.owner"),
        sortKey: "owner",
        sortKind: "text",
        getSortValue: (row) => row.owner_name,
        defaultWidth: 160,
        cell: (activity) => {
          const ownerPath = adminProfilePathForRole(
            activity.owner_user_id,
            activity.owner_role,
          );
          return ownerPath ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                navigate(ownerPath);
              }}
              className="break-words text-primary-600 hover:text-primary-700 hover:underline"
            >
              {activity.owner_name}
            </button>
          ) : (
            activity.owner_name
          );
        },
      },
      {
        id: "paymentModel",
        header: t("columns.paymentModel"),
        sortKey: "paymentModel",
        sortKind: "text",
        getSortValue: (row) => row.payment_model,
        defaultWidth: 160,
        cell: (activity) => renderPaymentModelCell(activity.payment_model),
      },
      {
        id: "region",
        header: tCommon("region.colCode"),
        sortKey: "region",
        sortKind: "text",
        getSortValue: (row) => row.region_code,
        defaultWidth: 100,
        cell: (activity) => <RegionCodeBadge code={activity.region_code} />,
      },
      {
        id: "commission",
        header: t("columns.commission"),
        sortKey: "commission",
        sortKind: "number",
        getSortValue: (row) => row.commission_rate,
        defaultWidth: 120,
        cell: (activity) => (
          <span className="tabular-nums">
            {formatCommissionPercentLabel(activity.commission_rate)}
          </span>
        ),
      },
    ],
    [activityDisplayName, navigate, t, tCommon],
  );

  return (
    <PageFrame title={t("title")} description={t("subtitle")} icon={Store}>
      <AdminListToolbar
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t("searchPlaceholder")}
        filterChildren={
          <>
            <AdminFilterMenu
            icon={LayoutGrid}
            name={t("filters.category")}
            value={categoryFilter}
            defaultValue="all"
            onChange={setCategoryFilter}
            options={categoryOptions}
          />
          <AdminFilterMenu
            icon={HandCoins}
            name={t("filters.paymentModel")}
            value={paymentModelFilter}
            defaultValue="all"
            onChange={setPaymentModelFilter}
            options={paymentModelOptions}
          />
          <AdminFilterMenu
            icon={Percent}
            name={t("filters.discounts")}
            value={discountFilter}
            defaultValue="all"
            onChange={setDiscountFilter}
            options={[
              {
                value: "all" as const,
                label: t("filters.discountsAll"),
                count: discountFilterCounts.all,
              },
              {
                value: "active" as const,
                label: t("filters.discountsActive"),
                count: discountFilterCounts.active,
              },
            ]}
          />
          <AdminStatusFilter
            value={statusFilter}
            onChange={setStatusFilter}
            counts={statusFilterCounts}
            labels={{
              name: t("filters.status"),
              all: t("filters.statusAll"),
              active: t("filters.statusActive"),
              inactive: t("filters.statusInactive"),
            }}
          />
          <AdminStatusFilter
            value={visibilityFilter}
            onChange={setVisibilityFilter}
            counts={visibilityFilterCounts}
            labels={{
              name: t("filters.visibility"),
              all: t("filters.visibilityAll"),
              active: t("filters.visibilityVisible"),
              inactive: t("filters.visibilityHidden"),
            }}
          />
          <AdminRegionFilter
            value={regionFilter}
            onChange={setRegionFilter}
            valueKey="id"
          />
          </>
        }
      />

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={filteredActivities}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="name"
        preferencesKey="admin-activities"
        resetToken={buildListResetToken([
          searchQuery,
          statusFilter,
          visibilityFilter,
          categoryFilter,
          paymentModelFilter,
          discountFilter,
          regionFilter ?? "",
        ])}
        loadState={
          isLoading
            ? { status: "loading" }
            : error
              ? {
                  status: "error",
                  message: t("loadError"),
                  retryLabel: t("retry"),
                  onRetry: () => void refetch(),
                }
              : undefined
        }
        emptyMessage={t("empty")}
        onRowClick={(activity) => navigate(`/activities/${activity.id}`)}
        openRowLabel={(activity) =>
          t("openRowAria", { name: activityDisplayName(activity) })
        }
      />
    </PageFrame>
  );
}
